import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/features/auth/hooks/useAuth";
import { CARD_STATUS_CODES } from "@/features/card/configs/card.config";
import { getUserCards } from "@/features/card/services/cards.service";
import type { EnhancedCard } from "@/features/card/types/card.type";
import { formatCardNumberWithSpaces } from "@/features/card/utils/format.util";

export type UseCardsReturn = {
  cards: EnhancedCard[]
  unactivatedCard?: EnhancedCard
  isLoading: boolean
  isError: boolean
};

export const toShortDate = (date: string) => {
  if (date.includes("/")) {
    const [month, year] = date.split("/");
    return {
      shortYear: year.slice(-2),
      year,
      month: month.padStart(2, "0"),
      day: "01",
    };
  }
  const [year, month, day] = date.split("-");
  return {
    shortYear: year.slice(-2),
    year,
    month,
    day,
  };
};

export const useCards = (): UseCardsReturn => {
  const { isAuthenticated } = useAuth();

  // isLoading, not isPending: a disabled query stays pending forever.
  const { data, isLoading, isError } = useQuery({
    queryKey: ["cards"],
    queryFn: getUserCards,
    enabled: isAuthenticated,
  });

  const cards = data?.map((card) => ({
    ...card,
    isActivated: card.is_live === 1,
    isUnlocked: card.status_code === CARD_STATUS_CODES.UNLOCK,
    formattedCardNumber: formatCardNumberWithSpaces(card.masked_pan),
    expireAt: toShortDate(card.expiry_date),
  })) ?? [];

  const unactivatedCard = cards.find((card) => card.is_live === 0);

  return {
    cards, unactivatedCard, isLoading, isError,
  };
};
